/**
 * Cart accessory cleanup
 *
 * Accessories added via the accessories grid carry a hidden line-item
 * property `_parent_product_id` pointing at the main product. If that
 * main product is no longer in the cart (customer removed it), the
 * orphaned accessory is automatically removed too so the customer
 * can't check out with just the bundled accessory.
 *
 * Runs:
 *   - once on page load (catches any cart state)
 *   - after cartUpdate events from the theme's pub/sub system (if present)
 */
(function () {
  let cleanupRunning = false;

  async function fetchCart() {
    const r = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!r.ok) throw new Error('cart fetch failed');
    return r.json();
  }

  async function removeLine(lineIndex) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ line: lineIndex, quantity: 0 }),
    });
  }

  async function cleanupOrphans({ reloadIfChanged = true } = {}) {
    if (cleanupRunning) return false;
    cleanupRunning = true;
    try {
      const cart = await fetchCart();
      if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) {
        return false;
      }

      // Build a set of product IDs currently in the cart.
      const productIdsInCart = new Set(cart.items.map((i) => i.product_id));

      // Walk items in reverse so line indexes don't shift underneath us.
      let removedAny = false;
      for (let i = cart.items.length - 1; i >= 0; i--) {
        const item = cart.items[i];
        const props = item.properties || {};
        const parentIdRaw = props._parent_product_id;
        if (!parentIdRaw) continue;

        const parentId = parseInt(parentIdRaw, 10);
        if (!parentId) continue;

        if (!productIdsInCart.has(parentId)) {
          // Orphan — parent product is not in cart anymore.
          await removeLine(i + 1); // Shopify cart lines are 1-based
          removedAny = true;
        }
      }

      if (removedAny && reloadIfChanged && window.location.pathname.includes('/cart')) {
        // On the cart page, reload so the customer sees the updated state.
        window.location.reload();
      }
      return removedAny;
    } catch (e) {
      console.warn('cart-accessory-cleanup:', e);
      return false;
    } finally {
      cleanupRunning = false;
    }
  }

  // Initial sweep on every page load.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => cleanupOrphans());
  } else {
    cleanupOrphans();
  }

  // If the theme uses a pub/sub cart event, re-run after cart updates.
  function hookPubSub() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS !== 'object') return;
    try {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        // Debounce a tiny bit so the change that triggered the event
        // has settled server-side.
        setTimeout(() => cleanupOrphans(), 120);
      });
    } catch (_) { /* no-op */ }
  }
  hookPubSub();

  // Also intercept cart change requests globally as a fallback for themes
  // that don't publish cartUpdate. We re-check after any /cart/change.js
  // or /cart/update.js response that the theme JS fires.
  const origFetch = window.fetch;
  window.fetch = function patchedFetch(input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const isCartMutation =
      url.includes('/cart/change') ||
      url.includes('/cart/update') ||
      url.includes('/cart/clear');
    const p = origFetch.apply(this, arguments);
    if (isCartMutation) {
      p.then(() => setTimeout(() => cleanupOrphans(), 120)).catch(() => {});
    }
    return p;
  };
})();
